import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Notification settings — Phase 10.
 * Reseller picks per-booking / daily / weekly / off.
 */
export default function NotificationSettings() {
  const { t } = useLanguage()
  const [mode, setMode] = useState('per_booking')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    tournamentApi.get('/portal/reseller/me')
      .then(r => setMode(r.data?.account?.NotificationMode || 'per_booking'))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])

  async function save(newMode) {
    setSaving(true); setErr(null)
    try {
      await tournamentApi.put('/portal/reseller/notifications', { notificationMode: newMode })
      setMode(newMode); setSavedAt(new Date())
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  if (loading) return <ResellerShell title={t('sp_notification_settings', 'Notification settings')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  return (
    <ResellerShell title={t('sp_notification_settings', 'Notification settings')}>
      <p style={{ color: '#64748b', maxWidth: 720, marginTop: 0, marginBottom: 24, fontSize: 14 }}>
        {t('sp_notifications_intro', 'How often do you want to be notified of new customer bookings? Customer-facing notifications (confirmation, my-trip portal, review invites) always send regardless.')}
      </p>
      {err && <div style={errorBox}>{err}</div>}
      {savedAt && <div style={successBox}>{t('sp_saved_at_time', 'Saved at {time}.', { time: savedAt.toLocaleTimeString() })}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 600 }}>
        <Option mode="per_booking"     active={mode === 'per_booking'}     onClick={() => save('per_booking')} disabled={saving}
          title={t('sp_notif_per_booking_title', 'Per booking')} desc={t('sp_notif_per_booking_desc', 'Email me every time a customer books.')} />
        <Option mode="daily_summary"   active={mode === 'daily_summary'}   onClick={() => save('daily_summary')} disabled={saving}
          title={t('sp_notif_daily_title', 'Daily summary')} desc={t('sp_notif_daily_desc', 'One digest per day at 9am with all bookings since the last one.')} />
        <Option mode="weekly_summary"  active={mode === 'weekly_summary'}  onClick={() => save('weekly_summary')} disabled={saving}
          title={t('sp_notif_weekly_title', 'Weekly summary')} desc={t('sp_notif_weekly_desc', 'One digest every Monday morning.')} />
        <Option mode="off"             active={mode === 'off'}             onClick={() => save('off')} disabled={saving}
          title={t('sp_notif_off_title', 'Off')} desc={t('sp_notif_off_desc', "Don't email me; I'll watch the dashboard.")} />
      </div>
    </ResellerShell>
  )
}

function Option({ active, disabled, onClick, title, desc }) {
  const { t } = useLanguage()
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: 16, background: '#fff', border: active ? '2px solid #00d19f' : '1px solid #e2e8f0',
      borderRadius: 8, cursor: disabled ? 'wait' : 'pointer', textAlign: 'left',
      fontFamily: 'inherit', fontSize: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 15, color: '#0f172a' }}>{title}</strong>
        {active && <span style={{ fontSize: 11, fontWeight: 700, color: '#00d19f', letterSpacing: 1 }}>{t('sp_chip_active', 'ACTIVE')}</span>}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{desc}</div>
    </button>
  )
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6, marginBottom: 16 }
